/**
 * Rune purchase rules — the shared (server + client) view of the two
 * battlefield abilities that modify rune shopping:
 *
 *  - RuneKeeper (RUNE_KEEPER_ABILITY_ID): +1 to the per-game rune purchase
 *    cap PER minion in play (stacking).
 *  - RuneFeast (RUNE_FEAST_ABILITY_ID): a flat 25% discount on every rune
 *    price while at least one is in play (does NOT stack).
 *
 * The SERVER reads these to validate/price purchases (authoritative); the
 * CLIENT mirrors them for display (the (x/N) counter, the shop prices, the
 * locked rows). Both read the SAME battlefield-only convention: only
 * minions actually IN PLAY count, hand cards have no effect (the Discount
 * convention). Both abilities are Runekeeper-class only (the catalogue
 * definitions carry `types: ['runekeeper']`): RuneKeeper is the Runekeeper's
 * base ability, RuneFeast is learnable from level 3.
 */

import { GameConfig } from '../../config/gameConfig';
import {
  RUNE_KEEPER_ABILITY_ID,
  RUNE_KEEPER_CAP_BONUS,
  RUNE_FEAST_ABILITY_ID,
  RUNE_FEAST_DISCOUNT_FRACTION,
} from './AbilityConstants';
import type { PlayerState } from './Player';
import type { RuneDefinition } from './RuneDefinitions';

/**
 * One entry per minion currently in play: that minion's ability IDs.
 * Battlefield order is irrelevant (all rules are counts/presence).
 */
export type BattlefieldAbilityLists = readonly (readonly string[])[];

/**
 * Server-side projection: the battlefield minion instances' ability lists
 * (unknown instance IDs — defensively — contribute no abilities).
 */
export function playerBattlefieldAbilities(player: PlayerState): BattlefieldAbilityLists {
  return player.battlefield.map(
    (instanceId) => player.minionInstances.get(instanceId)?.abilities ?? []
  );
}

/** Counts how many minions in play carry the given ability. */
export function countBattlefieldAbilities(
  inPlay: BattlefieldAbilityLists,
  abilityId: string
): number {
  let count = 0;
  for (const abilities of inPlay) {
    if (abilities.includes(abilityId)) count++;
  }
  return count;
}

/**
 * The player's EFFECTIVE per-game rune purchase cap right now:
 * the BASE cap (GameConfig.maxRunesPurchasable, 3 — pass an override to
 * honor a service's own config) plus RUNE_KEEPER_CAP_BONUS per RuneKeeper
 * minion in play (the effect stacks).
 *
 * The cap is checked at purchase time only: already-purchased runes are
 * never taken away — if a RuneKeeper leaves the board after purchases were
 * made under the raised cap, the cap shrinks back and further purchases are
 * blocked (e.g. 4 owned vs cap 3 — the "(4/3)" state), but the runes stay.
 */
export function getEffectiveRuneCap(
  inPlay: BattlefieldAbilityLists,
  baseCap: number = GameConfig.maxRunesPurchasable
): number {
  return baseCap + countBattlefieldAbilities(inPlay, RUNE_KEEPER_ABILITY_ID) * RUNE_KEEPER_CAP_BONUS;
}

/**
 * True while at least one RuneFeast minion is in play. The discount is a
 * FLAT RUNE_FEAST_DISCOUNT_FRACTION (25%) — it does not stack with a
 * second RuneFeast (and hand cards never activate it).
 */
export function isRuneFeastActive(inPlay: BattlefieldAbilityLists): boolean {
  return countBattlefieldAbilities(inPlay, RUNE_FEAST_ABILITY_ID) > 0;
}

/**
 * The current gold price of a rune for this purchase: the catalogue base
 * cost, or base × (1 − 25%) FLOORED while a RuneFeast is in play
 * (10g → 7g, 15g → 11g, 20g → 15g).
 */
export function getRuneGoldCost(
  rune: Pick<RuneDefinition, 'goldCost'>,
  inPlay: BattlefieldAbilityLists
): number {
  if (!isRuneFeastActive(inPlay)) return rune.goldCost;
  return Math.floor(rune.goldCost * (1 - RUNE_FEAST_DISCOUNT_FRACTION));
}
